import {
  InputGroup,
  InputRow,
  EmptyInput,
  CheckboxInput,
  useModelFormUpdate,
  FlexColumn,
  InputLabel,
  InputGroupBreak,
  ModelInput,
  errorsToPropertyErrors,
  ModelSortCode,
  Flex,
  Input,
  Button,
} from '@equifinance/shared';
import { v4 as uuid } from 'uuid';
import styled, { css } from 'styled-components';
import { useState } from 'react';
import { PaddedSection } from '../../components/Section';
import { Applicant, VerificationResultType } from '../../../../contexts/account/Account';
import { useAccountContext } from '../../../../contexts';
import { CrossCircleIcon, TickCircleIcon } from '../../components/Icons';

export const DirectDebitRefName = 'direct-debit';

const DueDateStyle = css`
  width:50px;
`;

const DueDateContainerCssStyle = css`
  flex:none;
`;

const VerificationContainer = styled(Flex)` ${({ theme }) => `
  flex: 1;
  align-items: center;
  gap: ${theme.padding[5]};
`}`;

export default function DirectDebitSection() {
  const { account, verifyBankAccount } = useAccountContext();

  const {
    directDebit,
    applicants,
    errors,
    warnings,
  } = account;

  const {
    canVerify, verificationOutcome, bankDetails,
  } = directDebit ?? {};

  const { verificationResult } = verificationOutcome ?? {};

  const propertyErrors = errorsToPropertyErrors(errors?.[bankDetails?.id] ?? []);

  const update = useModelFormUpdate();

  const onChangeNameOnAccount = (applicant: Applicant) => (value: boolean) => {
    if (value) {
      update({
        'directDebit.bankDetails.nameOnAccount': [
          ...(bankDetails?.nameOnAccount ?? []),
          {
            id: uuid(),
            applicantId: applicant?.id,
          },
        ],
      });
    } else {
      update({
        'directDebit.bankDetails.nameOnAccount': bankDetails?.nameOnAccount?.filter((v) => v?.applicantId !== applicant?.id),
      });
    }
  };

  const [nameOnAccountTouched, setNameOnAccountTouched] = useState<boolean>(false);

  const onVerify = () => {
    verifyBankAccount();
  };

  const verificationResultStatus = verificationResult ? (
    verificationResult === VerificationResultType.Pass ? <TickCircleIcon /> : <CrossCircleIcon />
  ) : <></>;

  return (
    <PaddedSection
      id={directDebit?.id}
      refName={DirectDebitRefName}
      title="Direct Debit"
      errors={errors}
      warnings={warnings}
    >
      <InputGroup>
        <InputRow>
          <Input
            id="name-on-account"
            errors={propertyErrors?.nameOnAccount ?? []}
            hasError={!!propertyErrors?.nameOnAccount?.length && nameOnAccountTouched}
          >
            <FlexColumn>
              <InputLabel required>Name on account</InputLabel>
              {
                applicants?.map((applicant, applicantIndex) => (
                  <CheckboxInput
                    id={`name-on-account-${applicantIndex}`}
                    label={`${(applicant?.personalDetails?.firstName ?? '')} ${(applicant?.personalDetails?.lastName ?? '')}`}
                    key={applicant?.id}
                    value={!!bankDetails?.nameOnAccount?.find(({ applicantId }) => applicantId === applicant?.id)}
                    onChange={onChangeNameOnAccount(applicant)}
                    disabled={applicants?.length === 1}
                    touchOverride={nameOnAccountTouched}
                    hasErrorOverride={(!!propertyErrors?.nameOnAccount?.length && nameOnAccountTouched)}
                    onBlur={() => setNameOnAccountTouched(true)}
                  />
                ))
              }
            </FlexColumn>
          </Input>
          <EmptyInput />
        </InputRow>
      </InputGroup>

      <InputGroupBreak />

      <InputGroup>
        <InputRow>
          <ModelInput
            label="Account number"
            field="directDebit.bankDetails.accountNumber"
            required
            mutateValue={(v) => v?.replace(/\D/g, '')?.slice(0, 8) ?? ''}
          />

          <ModelSortCode
            label="Sort code"
            field="directDebit.bankDetails.sortCode"
            required
          />
        </InputRow>

        <InputRow>
          <ModelInput
            label="Preferred due date"
            field="directDebit.preferredDueDate"
            placeholder="DD"
            required
            styles={DueDateStyle.toString()}
            inputWrapperCss={DueDateContainerCssStyle.toString()}
            mutateValue={(v) => {
              const input = v?.toString()?.replace(/\D/g, '');
              if (input.length === 2) return input;
              if (input.length > 2) return input.slice(1, 3);
              if (input.length === 1 && input !== '0') return `0${input}`;
              return input;
            }}
          />
          <VerificationContainer>
            <Button onClick={onVerify} small disabled={!canVerify}>Verify Details</Button>
            {verificationResultStatus}
          </VerificationContainer>
        </InputRow>
      </InputGroup>
    </PaddedSection>
  );
}
